import useTvApi, { messageOf } from "hooks/useTvApi";
import React, { useEffect, useState } from "react";
import { Card, Select, Toggle, useToast } from "stories/Admin";
import { DailyPoster, TvDailySettings, TvSettings } from "types/TvPosterTypes";
import {
  DAILY_BREAK_MINUTE_OPTIONS,
  DAILY_BREAK_SECOND_OPTIONS,
  DEFAULT_DAILY_SETTINGS,
  normalizeDailySettings,
} from "./tvOrder";
import styles from "./DailyPostersPanel.module.scss";

/*
 * «Dagens vers og hadith på TV»: plakatene iBønn lager hver dag av dagens vers
 * og dagens hadith. Moskeen velger om de skal vises først i plakatrotasjonen,
 * og/eller i fullskjerm med jevne mellomrom.
 */

interface Props {
  orgNr: string;
}

const DailyPostersPanel: React.FC<Props> = ({ orgNr }) => {
  const api = useTvApi();
  const toast = useToast();
  const [settings, setSettings] = useState<TvDailySettings>(
    DEFAULT_DAILY_SETTINGS,
  );
  const [today, setToday] = useState<DailyPoster[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgNr) return;
    let cancelled = false;
    api<TvSettings>(`/api/v1/mosques/${orgNr}/tv-settings`, "GET")
      .then((res) => !cancelled && setSettings(normalizeDailySettings(res)))
      .catch(() => !cancelled && setSettings(DEFAULT_DAILY_SETTINGS));
    api<DailyPoster[]>("/api/v1/tv/daily-posters/today", "GET")
      .then((res) => !cancelled && setToday(Array.isArray(res) ? res : []))
      .catch(() => !cancelled && setToday([]));
    return () => {
      cancelled = true;
    };
  }, [orgNr, api]);

  /** Lagrer bare feltene for dagens plakater; backend beholder resten. */
  const save = async (change: Partial<TvDailySettings>, done: string) => {
    const previous = settings;
    const next = { ...settings, ...change };
    setSettings(next);
    setSaving(true);
    try {
      const res = await api<TvSettings>(
        `/api/v1/mosques/${orgNr}/tv-settings`,
        "PUT",
        change,
      );
      setSettings(normalizeDailySettings(res));
      toast.success(done);
    } catch (e) {
      setSettings(previous);
      toast.error(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  const breakOn = settings.dailyBreakMinutes > 0;

  return (
    <Card
      title="Dagens vers og hadith på TV"
      subtitle="iBønn lager to nye plakater hver dag med arabisk tekst, norsk oversettelse og en kort refleksjon."
    >
      <div className={styles.previews}>
        {today === null ? null : today.length === 0 ? (
          <p className={styles.muted}>
            Dagens plakater er ikke klare ennå. De lages i løpet av dagen.
          </p>
        ) : (
          today.map((p) => (
            <figure key={p.id} className={styles.preview}>
              <img src={p.plakat} alt={p.title} />
              <img
                className={styles.portrait}
                src={p.plakatPortrait}
                alt={`${p.title}, stående`}
              />
              <figcaption>
                {p.kind === "QURAN" ? "Dagens vers" : "Dagens hadith"}
              </figcaption>
            </figure>
          ))
        )}
      </div>

      <div className={styles.options}>
        <Toggle
          label="Vis dagens vers først i plakatrotasjonen"
          checked={settings.showDailyQuran}
          disabled={saving}
          onChange={(e) =>
            save(
              { showDailyQuran: e.target.checked },
              e.target.checked
                ? "Dagens vers vises først på TV."
                : "Dagens vers vises ikke lenger i rotasjonen.",
            )
          }
        />
        <Toggle
          label="Vis dagens hadith først i plakatrotasjonen"
          checked={settings.showDailyHadith}
          disabled={saving}
          onChange={(e) =>
            save(
              { showDailyHadith: e.target.checked },
              e.target.checked
                ? "Dagens hadith vises først på TV."
                : "Dagens hadith vises ikke lenger i rotasjonen.",
            )
          }
        />
      </div>

      <section className={styles.break} aria-labelledby="tv-daily-break">
        <Toggle
          id="tv-daily-break"
          label="Vis dagens vers og hadith i fullskjerm med jevne mellomrom"
          checked={breakOn}
          disabled={saving}
          onChange={(e) =>
            save(
              {
                dailyBreakMinutes: e.target.checked ? 10 : 0,
                dailyBreakSeconds: settings.dailyBreakSeconds,
              },
              e.target.checked
                ? "Dagens vers og hadith vises i fullskjerm hvert 10. minutt."
                : "Fullskjermvisningen er slått av.",
            )
          }
        />
        <p className={styles.muted}>
          TV-en viser dagens vers og så dagens hadith i hele skjermen, og går
          tilbake til bønnetidene. Adhan, fellesbønn og tidsstyrte visninger
          går foran.
        </p>
        {breakOn && (
          <div className={styles.fields}>
            <Select
              label="Hvor ofte"
              value={settings.dailyBreakMinutes}
              disabled={saving}
              onChange={(e) =>
                save(
                  { dailyBreakMinutes: Number(e.target.value) },
                  `Vises hvert ${e.target.value}. minutt.`,
                )
              }
            >
              {DAILY_BREAK_MINUTE_OPTIONS(settings.dailyBreakMinutes).map(
                (m) => (
                  <option key={m} value={m}>
                    Hvert {m}. minutt
                  </option>
                ),
              )}
            </Select>
            <Select
              label="Tid per plakat"
              value={settings.dailyBreakSeconds}
              disabled={saving}
              onChange={(e) =>
                save(
                  { dailyBreakSeconds: Number(e.target.value) },
                  `Hver plakat vises i ${e.target.value} sekunder.`,
                )
              }
            >
              {DAILY_BREAK_SECOND_OPTIONS(settings.dailyBreakSeconds).map(
                (s) => (
                  <option key={s} value={s}>
                    {s < 60 ? `${s} sekunder` : `${s / 60} min`}
                  </option>
                ),
              )}
            </Select>
          </div>
        )}
      </section>
    </Card>
  );
};

export default DailyPostersPanel;
